/**
 * Text Utilities
 * Helper functions for text processing and formatting
 */

/**
 * Converts URLs in text to clickable hyperlinks
 * Supports http://, https://, and www. patterns
 * @param {string} text - The text containing URLs
 * @returns {Array} - Array of text/link components for React rendering
 */
export const linkifyText = (text) => {
  if (!text) return [];

  // Regular expression to match URLs
  const urlPattern = /(\b(https?:\/\/|www\.)[^\s<]+[^<.,:;"')\]\s])/gi;
  
  const parts = [];
  let lastIndex = 0;
  let match;

  // Create a new regex instance for iteration
  const regex = new RegExp(urlPattern);
  
  while ((match = regex.exec(text)) !== null) {
    const url = match[0];
    const index = match.index;
    
    // Add text before the URL
    if (index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, index)
      });
    }
    
    // Add the URL as a link
    // Ensure URL has protocol
    let href = url;
    if (url.startsWith('www.')) {
      href = 'https://' + url;
    }
    
    parts.push({
      type: 'link',
      content: url,
      href: href
    });
    
    lastIndex = regex.lastIndex;
  }
  
  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }
  
  return parts.length > 0 ? parts : [{ type: 'text', content: text }];
};

/**
 * React component helper to render linkified text
 * Usage: {renderLinkifiedText(comment.content)}
 */
export const renderLinkifiedText = (text) => {
  const parts = linkifyText(text);
  
  return parts.map((part, index) => {
    if (part.type === 'link') {
      return (
        <a
          key={index}
          href={part.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline break-all"
        >
          {part.content}
        </a>
      );
    }
    return <span key={index}>{part.content}</span>;
  });
};
